/**
 * Shortlist storage — Phase A · Feature 2.
 *
 * Lightweight per-project favorites that live in localStorage so a
 * sales rep can build up a shortlist on a buyer's iPad without any
 * login. The same primitives feed Compare (Feature 3) and the bulk
 * Unit PDF Offer (Feature 5).
 *
 * Storage key shape:
 *     evb.shortlist.<projectSlug> = ["A-101", "A-203", ...]
 *
 * Subscribers are notified via the in-page `evb-shortlist-change` event
 * so multiple components can react without a global state library.
 */

const KEY = (slug) => `evb.shortlist.${slug}`;
const EVENT = "evb-shortlist-change";

const read = (slug) => {
  try {
    const raw = localStorage.getItem(KEY(slug));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
};

const write = (slug, codes) => {
  try {
    localStorage.setItem(KEY(slug), JSON.stringify(codes));
  } catch {
    /* quota / private mode — fail silent */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { slug, codes } }));
};

export const getShortlist = (slug) => read(slug);

export const isShortlisted = (slug, code) => read(slug).includes(code);

export const addToShortlist = (slug, code) => {
  const cur = read(slug);
  if (!code || cur.includes(code)) return cur;
  const next = [...cur, code];
  write(slug, next);
  return next;
};

export const removeFromShortlist = (slug, code) => {
  const next = read(slug).filter((c) => c !== code);
  write(slug, next);
  return next;
};

export const clearShortlist = (slug) => {
  write(slug, []);
  return [];
};

export const toggleShortlist = (slug, code) =>
  (isShortlisted(slug, code) ? removeFromShortlist : addToShortlist)(slug, code);

/**
 * React hook — returns the live shortlist for the given slug and
 * re-renders whenever it changes (including from other tabs).
 */
import { useEffect, useState } from "react";

export function useShortlist(slug) {
  const [codes, setCodes] = useState(() => read(slug));
  useEffect(() => {
    if (!slug) return;
    setCodes(read(slug));
    const onChange = (e) => {
      if (!e.detail || e.detail.slug === slug) setCodes(read(slug));
    };
    const onStorage = (e) => {
      if (e.key === KEY(slug)) setCodes(read(slug));
    };
    window.addEventListener(EVENT, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, [slug]);
  return codes;
}
